import { useCart } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { items, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[400px] bg-[#0B1B3A] shadow-2xl z-50 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#162D52]">
            <h2 className="text-xl font-serif text-[#F5F0E8]">Shopping Cart</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center hover:bg-[#162D52] rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-[#F5F0E8] text-xl"></i>
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-6">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <i className="ri-shopping-bag-line text-[#6B83A8] text-6xl mb-4"></i>
                <p className="text-[#8A9BB5] text-sm">Your cart is empty</p>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="bg-[#132847] rounded-lg p-4">
                    <div className="flex gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#1A3358] flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-serif text-[#F5F0E8] mb-1 truncate">
                          {item.name}
                        </h3>
                        <div className="flex items-center gap-1 mb-2">
                          <i className="ri-map-pin-line text-[#6B83A8] text-xs"></i>
                          <p className="text-xs text-[#6B83A8] uppercase tracking-wider">
                            {item.destination}
                          </p>
                        </div>
                        <p className="text-sm font-medium text-[#D4C5A9]">{item.price}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-3 bg-[#0B1B3A] rounded-lg p-1">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#162D52] rounded transition-colors cursor-pointer"
                        >
                          <i className="ri-subtract-line text-[#F5F0E8]"></i>
                        </button>
                        <span className="text-[#F5F0E8] text-sm w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#162D52] rounded transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line text-[#F5F0E8]"></i>
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-[#8A9BB5] hover:text-[#F5F0E8] transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-[#162D52] p-6 space-y-4">
              <div className="bg-[#2A1A1A] border border-[#5A3030] rounded-lg p-3 flex items-center gap-2">
                <i className="ri-information-line text-[#E8A0A0] text-base"></i>
                <p className="text-xs text-[#E8A0A0]">All items are currently sold out. Checkout is unavailable.</p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#8A9BB5] text-sm uppercase tracking-wider">
                  Subtotal
                </span>
                <span className="text-xl font-medium text-[#D4C5A9]">
                  CA${getCartTotal().toFixed(2)}
                </span>
              </div>
              <button
                disabled
                className="w-full bg-[#162D52] text-[#6B83A8] py-3 rounded-lg font-medium cursor-not-allowed whitespace-nowrap"
              >
                Checkout Unavailable
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
